/**
 * Update Account Step Component
 * Uses same pattern as CreateAccount with StepContent
 */

import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { RootState } from '../../../../services/serviceTypes';
import type { StepFormMessage } from '../../data-layer/types';
import { setSavedStepData } from '../../data-store/copilotSlice';
import { FORM_RESPONSE_TEXTS } from '../../services/responseTextService';
import ErrorBanner from '../ErrorBanner/ErrorBanner';
import ExpandableForm from '../ExpandableForm/ExpandableForm';
import ResponseWithForm from '../ResponseWithForm/ResponseWithForm';
import StepRenderer from '../StepRenderer/StepRenderer';

interface UpdateAccountStepProps {
  message: StepFormMessage;
  onFormSubmit: (
    stepName: string,
    formData: Record<string, unknown>,
    isValid: boolean
  ) => void;
  onSkip: (currentStep: string, nextStep: string) => void;
}

/**
 * UpdateAccountStep component for handling account update configuration
 */
function UpdateAccountStep({
  message,
  onSkip,
  onFormSubmit,
}: UpdateAccountStepProps) {
  const [validationError, setValidationError] = useState<string | null>(null);
  const dispatch = useDispatch();

  // Get persisted form data from Redux
  const persistedFormData = useSelector(
    (state: RootState) => state.copilot.stepFormData.UpdateAccount
  );

  // Check if in edit mode
  const isInEditMode = useSelector(
    (state: RootState) => state.copilot.ui.mode === 'edit'
  );

  // Check if step is completed
  const isCompletedCheck = useSelector(
    (state: RootState) => state.copilot.navigation.completedSteps ?? {}
  );

  const skippedSteps = useSelector(
    (state: RootState) => state.copilot.navigation.skippedSteps ?? []
  );

  const btnlabel = isCompletedCheck.includes(message.stepName ?? '');
  const isStepCompletedOrSkipped =
    isCompletedCheck.includes('UpdateAccount') ||
    skippedSteps.includes('UpdateAccount');

  const validateAndContinue = useCallback(() => {
    setValidationError(null);

    // Save to savedStepData (assuming validation always passes for this step)
    dispatch(
      setSavedStepData({
        stepName: 'UpdateAccount',
        formData: persistedFormData,
      })
    );

    onFormSubmit('UpdateAccount', persistedFormData, true);

    return !isInEditMode;
  }, [dispatch, persistedFormData, onFormSubmit, isInEditMode]);

  const handleSkipStep = () => {
    onSkip('UpdateAccount', 'EnableAccount');
  };

  return (
    <ResponseWithForm
      responseText={
        !isInEditMode
          ? FORM_RESPONSE_TEXTS.UPDATE_ACCOUNT
          : FORM_RESPONSE_TEXTS.UPDATE_ACCOUNT_EDIT
      }>
      <ExpandableForm
        stepName={message.stepName ?? 'UpdateAccount'}
        heading={message.heading}
        buttonLabel={btnlabel ? 'Update' : message.buttonLabel}
        buttonkind='outlined'
        onSave={validateAndContinue}
        onSecondory={handleSkipStep}
        secondryButtonLabel={isInEditMode ? undefined : 'Skip'}
        isSecondaryDisabled={isStepCompletedOrSkipped}>
        <StepRenderer stepName='UpdateAccount' />
        {validationError && <ErrorBanner message={validationError} />}
      </ExpandableForm>
    </ResponseWithForm>
  );
}

export default UpdateAccountStep;
